'use client';
import { useState, useEffect } from 'react';
import { GoArrowLeft } from 'react-icons/go';
import { computeCountryDropdownValues } from 'utils/country';
import useStatesByCountry from 'hooks/useStatesByCountry';

const countryDropdownValues = computeCountryDropdownValues();

export default function ContactDetailsStep({ formik, onBack }) {
  const defaultSelectedCountry = formik.values.userPersonalAddress?.country || 'US';
  const [country, setCountry] = useState(defaultSelectedCountry);

  const { statesList, isFetchingStates } = useStatesByCountry(country);
  const [state, setState] = useState('');

  // Clear state when country changes and no states available
  useEffect(() => {
    if (!statesList?.length) {
      setState('');
      formik.setFieldValue('userPersonalAddress.state', '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, statesList]);

  // Update formik values when local state changes
  useEffect(() => {
    formik.setFieldValue('userPersonalAddress.country', country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    formik.setFieldValue('userPersonalAddress.state', state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    // Reset touched state for address fields when component mounts
    formik.setTouched({
      ...formik.touched,
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
      userPersonalAddress: {
        line1: false,
        line2: false,
        city: false,
        state: false,
        postalCode: false,
        country: false,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        className="hover:text-blue-60 mb-[0.5rem] flex items-center gap-[0.5rem] text-[1rem] font-medium text-[#191919] transition-colors"
      >
        <GoArrowLeft size={18} />
        <span className="text-base font-bold">Back</span>
      </button>

      <h2 className="mb-[0.5rem] text-[1.65rem] font-[500] leading-tight text-[#191919]">
        Who Is The Main Contact For This New Business?
      </h2>
      <p className="mb-[0.5rem] text-[1rem] font-normal text-[#757575]">
        This is who we'll contact if more info is needed during the filing process.
      </p>

      {/* Name Fields */}
      <div className="mb-[0.5rem] flex flex-col gap-[1rem] md:flex-row">
        {/* First Name */}
        <div className="w-full">
          <label htmlFor="firstName" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
              formik.touched.firstName && formik.errors.firstName ? 'border-red-500' : 'border-[#E5E7EB]'
            }`}
            {...formik.getFieldProps('firstName')}
          />
          {formik.touched.firstName && formik.errors.firstName && (
            <div className="mt-1 text-sm text-red-500">{formik.errors.firstName}</div>
          )}
        </div>

        {/* Last Name */}
        <div className="w-full">
          <label htmlFor="lastName" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
              formik.touched.lastName && formik.errors.lastName ? 'border-red-500' : 'border-[#E5E7EB]'
            }`}
            {...formik.getFieldProps('lastName')}
          />
          {formik.touched.lastName && formik.errors.lastName && (
            <div className="mt-1 text-sm text-red-500">{formik.errors.lastName}</div>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="mb-[0.5rem] mt-[0.6rem]">
        <label htmlFor="email" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
          Email
        </label>
        <input
          id="email"
          type="email"
          className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
            formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-[#E5E7EB]'
          }`}
          {...formik.getFieldProps('email')}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="mt-1 text-sm text-red-500">{formik.errors.email}</div>
        )}
      </div>

      {/* Address Line 1 */}
      <div className="mb-[0.5rem] mt-[0.6rem]">
        <label htmlFor="addressLine1" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
          Address Line 1
        </label>
        <input
          id="addressLine1"
          type="text"
          className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
            formik.touched.userPersonalAddress?.line1 && formik.errors.userPersonalAddress?.line1
              ? 'border-red-500'
              : 'border-[#E5E7EB]'
          }`}
          placeholder="Address Line 1"
          {...formik.getFieldProps('userPersonalAddress.line1')}
        />
        {formik.touched.userPersonalAddress?.line1 && formik.errors.userPersonalAddress?.line1 && (
          <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.line1}</div>
        )}
      </div>

      {/* Address Line 2 and City */}
      <div className="mb-[0.5rem] mt-[0.6rem] flex flex-col gap-[1rem] md:flex-row">
        {/* Address Line 2 */}
        <div className="w-full">
          <label htmlFor="addressLine2" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
            Address Line 2
          </label>
          <input
            id="addressLine2"
            type="text"
            className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
              formik.touched.userPersonalAddress?.line2 && formik.errors.userPersonalAddress?.line2
                ? 'border-red-500'
                : 'border-[#E5E7EB]'
            }`}
            placeholder="Address Line 2"
            {...formik.getFieldProps('userPersonalAddress.line2')}
          />
          {formik.touched.userPersonalAddress?.line2 && formik.errors.userPersonalAddress?.line2 && (
            <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.line2}</div>
          )}
        </div>

        {/* City */}
        <div className="w-full">
          <label htmlFor="city" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
            City
          </label>
          <input
            id="city"
            type="text"
            className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
              formik.touched.userPersonalAddress?.city && formik.errors.userPersonalAddress?.city
                ? 'border-red-500'
                : 'border-[#E5E7EB]'
            }`}
            placeholder="City"
            {...formik.getFieldProps('userPersonalAddress.city')}
          />
          {formik.touched.userPersonalAddress?.city && formik.errors.userPersonalAddress?.city && (
            <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.city}</div>
          )}
        </div>
      </div>

      {/* ZIP and State */}
      <div
        className={`mb-[0.5rem] mt-[0.6rem] flex flex-col gap-[1rem] md:flex-row ${statesList?.length > 0 ? '' : 'md:grid-cols-1'}`}
      >
        {/* ZIP */}
        <div className="w-full">
          <label htmlFor="postalCode" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
            ZIP
          </label>
          <input
            id="postalCode"
            type="text"
            className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
              formik.touched.userPersonalAddress?.postalCode && formik.errors.userPersonalAddress?.postalCode
                ? 'border-red-500'
                : 'border-[#E5E7EB]'
            }`}
            placeholder="Zip"
            {...formik.getFieldProps('userPersonalAddress.postalCode')}
          />
          {formik.touched.userPersonalAddress?.postalCode && formik.errors.userPersonalAddress?.postalCode && (
            <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.postalCode}</div>
          )}
        </div>

        {/* State */}
        {statesList?.length > 0 && (
          <div className="w-full">
            <label htmlFor="state" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
              State
            </label>
            <select
              id="state"
              name="state"
              className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] focus:outline-none focus:ring-2 focus:ring-therablue ${
                formik.touched.userPersonalAddress?.state && formik.errors.userPersonalAddress?.state
                  ? 'border-red-500'
                  : 'border-[#E5E7EB]'
              }`}
              value={state}
              onChange={(e) => {
                setState(e.target.value);
              }}
              disabled={isFetchingStates}
            >
              <option value="">Select</option>
              {statesList.map((stateOption) => (
                <option key={stateOption.value} value={stateOption.value}>
                  {stateOption.label}
                </option>
              ))}
            </select>
            {formik.touched.userPersonalAddress?.state && formik.errors.userPersonalAddress?.state && (
              <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.state}</div>
            )}
          </div>
        )}
      </div>

      {/* Country */}
      <div className="mb-[0.5rem] mt-[0.6rem]">
        <label htmlFor="country" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
          Country
        </label>
        <select
          id="country"
          name="country"
          className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] focus:outline-none focus:ring-2 focus:ring-therablue ${
            formik.touched.userPersonalAddress?.country && formik.errors.userPersonalAddress?.country
              ? 'border-red-500'
              : 'border-[#E5E7EB]'
          }`}
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
          }}
        >
          {countryDropdownValues.map((countryOption) => (
            <option key={countryOption.value} value={countryOption.value}>
              {countryOption.label}
            </option>
          ))}
        </select>
        {formik.touched.userPersonalAddress?.country && formik.errors.userPersonalAddress?.country && (
          <div className="mt-1 text-sm text-red-500">{formik.errors.userPersonalAddress.country}</div>
        )}
      </div>

      {/* Phone */}
      <div className="mb-[0.5rem] mt-[0.6rem]">
        <label htmlFor="phone" className="mb-[0.5rem] block text-[1rem] font-medium text-[#191919]">
          Phone
        </label>
        <input
          id="phone"
          type="text"
          className={`w-full rounded-lg border bg-white px-4 py-[0.625rem] text-[1rem] text-[#191919] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-therablue ${
            formik.touched.phone && formik.errors.phone ? 'border-red-500' : 'border-[#E5E7EB]'
          }`}
          {...formik.getFieldProps('phone')}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className="mt-1 text-sm text-red-500">{formik.errors.phone}</div>
        )}
      </div>
    </>
  );
}
